'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import type { DestinationInput } from './VisaMatrixInteractive';

interface DestinationSelectorProps {
  destinations: DestinationInput[];
  onAddDestination: (destination: DestinationInput) => void;
  onRemoveDestination: (id: string) => void;
}

export default function DestinationSelector({
  destinations,
  onAddDestination,
  onRemoveDestination,
}: DestinationSelectorProps) {
  const [country, setCountry] = useState('');
  const [nationality, setNationality] = useState('United Kingdom');
  const [travelPurpose, setTravelPurpose] = useState('tourism');
  const [stayDuration, setStayDuration] = useState(14);

  const popularDestinations = [
    'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany',
    'France', 'Spain', 'Italy', 'Japan', 'Singapore', 'Thailand',
    'United Arab Emirates', 'India', 'China', 'Brazil'
  ];

  const nationalities = [
    'United Kingdom', 'United States', 'India', 'China', 'Germany',
    'France', 'Australia', 'Canada', 'Brazil', 'Nigeria', 'Pakistan'
  ];

  const handleAdd = () => {
    if (!country) return;

    const newDestination: DestinationInput = {
      id: `dest-${Date.now()}`,
      country,
      nationality,
      travelPurpose,
      stayDuration,
    };

    onAddDestination(newDestination);
    setCountry('');
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 border-2 border-blue-100">
      <h2 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3">
        <Icon name="PlusCircleIcon" size={28} className="text-blue-600" />
        Add Destinations to Compare
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {/* Destination Country */}
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">
            Destination Country
          </label>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select country...</option>
            {popularDestinations.map((dest) => (
              <option key={dest} value={dest}>{dest}</option>
            ))}
          </select>
        </div>

        {/* Nationality */}
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">
            Your Nationality
          </label>
          <select
            value={nationality}
            onChange={(e) => setNationality(e.target.value)}
            className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {nationalities.map((nat) => (
              <option key={nat} value={nat}>{nat}</option>
            ))}
          </select>
        </div>

        {/* Travel Purpose */}
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">
            Travel Purpose
          </label>
          <select
            value={travelPurpose}
            onChange={(e) => setTravelPurpose(e.target.value)}
            className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="tourism">Tourism</option>
            <option value="business">Business</option>
            <option value="study">Study</option>
            <option value="work">Work</option>
            <option value="transit">Transit</option>
          </select>
        </div>

        {/* Stay Duration */}
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">
            Stay Duration (days)
          </label>
          <input
            type="number"
            value={stayDuration}
            onChange={(e) => setStayDuration(parseInt(e.target.value) || 0)}
            min="1"
            max="365"
            className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Add Button */}
        <div className="flex items-end">
          <button
            onClick={handleAdd}
            disabled={!country}
            className="w-full px-6 py-2.5 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Icon name="PlusIcon" size={20} />
            <span>Add</span>
          </button>
        </div>
      </div>

      {/* Selected Destinations */}
      {destinations.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-gray-700 mb-3">
            Selected Destinations ({destinations.length}/5)
          </h3>
          <div className="flex flex-wrap gap-3">
            {destinations.map((dest) => (
              <div
                key={dest.id}
                className="flex items-center gap-2 px-4 py-2 bg-blue-50 border-2 border-blue-200 rounded-lg"
              >
                <Icon name="MapPinIcon" size={16} className="text-blue-600" />
                <span className="text-sm font-bold text-gray-900">{dest.country}</span>
                <span className="text-xs text-gray-600">({dest.nationality})</span>
                <button
                  onClick={() => onRemoveDestination(dest.id)}
                  className="ml-2 p-1 hover:bg-blue-100 rounded transition-colors"
                >
                  <Icon name="XMarkIcon" size={16} className="text-gray-600" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}